import React, { useCallback, useMemo, useState } from 'react';
import { Upload, FileText, ArrowRight, ChevronDown, CheckCircle, ArrowLeft, X } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { toast } from 'sonner';
export function UploadPage({ onBack, onComplete }) {
  const [step, setStep] = useState('upload');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOptionalQuestions, setShowOptionalQuestions] = useState(false);

  const [audienceType, setAudienceType] = useState('');
  const [presentationGoal, setPresentationGoal] = useState('');
  const [audienceSize, setAudienceSize] = useState('');
  const [researchClient, setResearchClient] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [analysisFocus, setAnalysisFocus] = useState('both'); // 'content', 'design', or 'both'
  const [hasVideos, setHasVideos] = useState(false);
  const [fastAnalysis, setFastAnalysis] = useState(true);
  const [expertiseLevel, setExpertiseLevel] = useState('beginner'); // 'beginner' or 'expert'
  const [generateEditedPreview, setGenerateEditedPreview] = useState(false);
  const [nanoBananaModel, setNanoBananaModel] = useState('flash'); // 'flash' or 'pro'

  const getFocusLabel = (value) => {
    if (value === 'content') return 'Content Only';
    if (value === 'design') return 'Design Only';
    return 'Content + Design';
  };

  const handleFileUpload = useCallback((event) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  }, []);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (!file) {
      return;
    }

    const isPresentation = file.type.includes('presentation') || file.name.endsWith('.pptx') || file.name.endsWith('.ppt');
    if (!isPresentation) {
      toast.error('Please upload a PowerPoint presentation (.pptx or .ppt).');
      return;
    }

    setUploadedFile(file);
  }, []);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
  }, []);

  const handleUploadAndProceed = async () => {
    if (!uploadedFile) {
      toast.error('Select a PowerPoint file to continue.');
      return;
    }

    setIsUploading(true);
    await new Promise((resolve) => setTimeout(resolve, 1200));
    setIsUploading(false);
    setStep('questions');
  };

  const projectSummary = useMemo(() => {
    if (!uploadedFile) {
      return {
        name: 'No file selected',
        size: null,
      };
    }

    return {
      name: uploadedFile.name,
      size: `${Math.round(uploadedFile.size / 1024)} KB`,
    };
  }, [uploadedFile]);

  const buildProjectPayload = useCallback(() => ({
    file: uploadedFile,
    filename: uploadedFile?.name || 'Untitled Presentation',
    uploadedAt: new Date().toISOString(),
    audienceInfo: {
      audienceType,
      presentationGoal,
      audienceSize,
      researchClient,
      additionalContext,
      analysisFocus,
      hasVideos,
      fastAnalysis,
      expertiseLevel,
      generateEditedPreview,
      nanoBananaModel,
    },
  }), [uploadedFile, audienceType, presentationGoal, audienceSize, researchClient, additionalContext, analysisFocus, hasVideos, fastAnalysis, expertiseLevel, generateEditedPreview, nanoBananaModel]);

  const completeUpload = async () => {
    if (!uploadedFile || isSubmitting) {
      if (!uploadedFile) {
        toast.error('Select a PowerPoint file to continue.');
      }
      return;
    }
    try {
      setIsSubmitting(true);
      onComplete?.(buildProjectPayload());
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipQuestions = async () => {
    await completeUpload();
  };

  const handleCompleteWithQuestions = async () => {
    await completeUpload();
  };

  const handleBackClick = () => {
    if (step === 'upload') {
      onBack?.();
      return;
    }

    if (step === 'processing') {
      return;
    }

    if (step === 'focus') {
      setStep('questions');
      return;
    }

    setStep('upload');
  };

  return (
    <div className="relative min-h-screen bg-background">
      <div className="absolute inset-0 bg-gradient-to-br from-accent/10 via-transparent to-primary/10 pointer-events-none"></div>

      <div className="relative z-10 mx-auto flex max-w-6xl flex-col gap-10 px-6 py-12">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" className="h-10 rounded-lg border border-border/40 bg-background/70 px-3 text-sm text-muted-foreground hover:text-foreground" onClick={handleBackClick}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Upload Presentation</h1>
              <p className="text-base text-muted-foreground">Provide your deck and optional context to start the Design Compass analysis.</p>
            </div>
          </div>
        </div>

        {step === 'upload' && (
          <Card className="border-border/50 bg-card/90 backdrop-blur">
            <CardHeader>
              <CardTitle className="text-xl font-semibold">Step 1 · Upload your deck</CardTitle>
              <CardDescription>Drag & drop your PowerPoint file or browse from your device.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {!uploadedFile ? (
                <>
                  <div
                    className="relative flex min-h-[220px] flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed border-border/60 bg-muted/20 p-8 text-center transition duration-200 hover:border-accent/60 hover:bg-muted/30"
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                  >
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-accent/10">
                      <Upload className="h-8 w-8 text-accent" />
                    </div>
                    <div className="space-y-2">
                      <p className="text-lg font-semibold text-foreground">
                        Drop your presentation here
                      </p>
                      <p className="text-sm text-muted-foreground">
                        We support .pptx and .ppt files up to 150MB. Your data stays secure.
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-3">
                      <Button onClick={() => document.getElementById('presentation-upload-input')?.click()} className="bg-gradient-to-r from-accent to-accent/90 text-accent-foreground hover:from-accent/90 hover:to-accent/80 shadow-lg">
                        Browse files
                      </Button>
                      <div className="text-xs text-muted-foreground">
                        or drag and drop
                      </div>
                    </div>
                    <input id="presentation-upload-input" type="file" accept=".ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation" className="hidden" onChange={handleFileUpload} />
                    <div className="absolute inset-0 rounded-2xl border border-border/40 pointer-events-none" />
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-8">
                  <Card className="w-full max-w-md bg-card/80 backdrop-blur border border-border/50">
                    <CardContent className="flex items-center gap-4 p-6">
                      <div className="rounded-xl bg-accent/10 p-4">
                        <FileText className="h-10 w-10 text-accent" />
                      </div>
                      <div className="flex-1">
                        <p className="text-lg font-medium text-foreground">{projectSummary.name}</p>
                        {projectSummary.size && (
                          <p className="text-sm text-muted-foreground">{projectSummary.size}</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setUploadedFile(null)}
                        className="h-8 w-8 rounded-full"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </CardContent>
                  </Card>
                  <p className="mt-4 text-sm text-muted-foreground">
                    File uploaded successfully. Continue to the next step below.
                  </p>
                </div>
              )}

              <div className="grid gap-4 md:grid-cols-2">
                <Card className="border-border/40 bg-background/60">
                  <CardContent className="flex items-start gap-4 p-5">
                    <div className="rounded-lg bg-accent/10 p-2">
                      <CheckCircle className="h-5 w-5 text-accent" />
                    </div>
                    <div className="space-y-2 text-sm">
                      <p className="font-semibold text-foreground">Preferred decks</p>
                      <p className="text-muted-foreground">Executive presentations, sales pitches, strategy updates, investment memos, and training decks.</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-border/40 bg-background/60">
                  <CardContent className="flex items-start gap-4 p-5">
                    <div className="rounded-lg bg-accent/10 p-2">
                      <ChevronDown className="h-5 w-5 text-accent" />
                    </div>
                    <div className="space-y-2 text-sm">
                      <p className="font-semibold text-foreground">File tips</p>
                      <p className="text-muted-foreground">Remove hidden slides and ensure fonts are embedded for the best analysis quality.</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="flex items-center justify-end gap-3">
                <Button variant="outline" onClick={handleBackClick} className="border-border text-muted-foreground hover:text-foreground">
                  Cancel
                </Button>
                <Button onClick={handleUploadAndProceed} disabled={!uploadedFile || isUploading} className="bg-gradient-to-r from-accent to-accent/90 text-accent-foreground hover:from-accent/90 hover:to-accent/80 shadow-lg disabled:opacity-50">
                  {isUploading ? 'Uploading…' : 'Continue'}
                  {!isUploading && <ArrowRight className="ml-2 h-4 w-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 'questions' && (
          <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
            <Card className="border-border/50 bg-card/90 backdrop-blur">
              <CardHeader>
                <CardTitle className="text-xl font-semibold">Step 2 · Audience insights (optional)</CardTitle>
                <CardDescription>Help the AI tailor recommendations by telling us more about the presentation context.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="audience-type">Audience type</Label>
                    <Select value={audienceType} onValueChange={setAudienceType}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select audience type (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="executives">Executives</SelectItem>
                        <SelectItem value="technical">Technical team</SelectItem>
                        <SelectItem value="sales">Sales team</SelectItem>
                        <SelectItem value="clients">Clients</SelectItem>
                        <SelectItem value="investors">Investors</SelectItem>
                        <SelectItem value="general">General audience</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="presentation-goal">Presentation goal</Label>
                    <Select value={presentationGoal} onValueChange={setPresentationGoal}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select goal (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="inform">Inform</SelectItem>
                        <SelectItem value="persuade">Persuade</SelectItem>
                        <SelectItem value="sell">Sell</SelectItem>
                        <SelectItem value="educate">Educate</SelectItem>
                        <SelectItem value="update">Update</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="audience-size">Audience size</Label>
                    <Select value={audienceSize} onValueChange={setAudienceSize}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select size (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="small">Small (1-10)</SelectItem>
                        <SelectItem value="medium">Medium (11-50)</SelectItem>
                        <SelectItem value="large">Large (50+)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="research-client">Client (optional)</Label>
                    <Input id="research-client" value={researchClient} onChange={(event) => setResearchClient(event.target.value)} placeholder="Client or brand name" className="bg-input border-border" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="additional-context">Additional context</Label>
                  <Textarea id="additional-context" value={additionalContext} onChange={(event) => setAdditionalContext(event.target.value)} placeholder="Key messages, stakeholder expectations, or anything else you'd like the AI to consider." className="min-h-[120px] bg-input border-border" />
                </div>

                <Collapsible open={showOptionalQuestions} onOpenChange={setShowOptionalQuestions}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 text-sm text-muted-foreground">
                      <ChevronDown className={`h-4 w-4 transition-transform ${showOptionalQuestions ? 'rotate-180' : ''}`} />
                      Advanced questions
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-4 space-y-4">
                    <div className="space-y-2">
                      <Label>Decision deadline</Label>
                      <Input placeholder="When does the audience need to decide?" className="bg-input border-border" />
                    </div>
                    <div className="space-y-2">
                      <Label>Key stakeholder</Label>
                      <Input placeholder="Who owns the final sign-off?" className="bg-input border-border" />
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                <div className="flex items-center justify-between gap-3 border-t border-border/40 pt-4">
                  <Button variant="outline" onClick={() => setStep('upload')} className="border-border text-muted-foreground hover:text-foreground">
                    Back
                  </Button>
                  <div className="flex items-center gap-3">
                    <Button variant="outline" onClick={handleSkipQuestions} className="border-border text-muted-foreground hover:text-foreground">
                      Skip for now
                    </Button>
                    <Button onClick={() => setStep('focus')} className="bg-gradient-to-r from-accent to-accent/90 text-accent-foreground hover:from-accent/90 hover:to-accent/80 shadow-lg">
                      Continue
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/50 bg-card/80 backdrop-blur">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">What happens next?</CardTitle>
                <CardDescription>We combine your deck with the Design Compass knowledge base to craft actionable guidance.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-xl border border-border/40 bg-background/60 p-4 text-sm text-muted-foreground">
                  <p className="font-semibold text-foreground">Analysis checklist</p>
                  <ul className="mt-3 space-y-2">
                    <li>• Structural narrative review</li>
                    <li>• Slide-by-slide clarity scoring</li>
                    <li>• Visual and tone alignment</li>
                    <li>• Client-ready recommendations</li>
                  </ul>
                </div>
                <div className="rounded-xl border border-border/40 bg-background/60 p-4 text-sm text-muted-foreground">
                  <p className="font-semibold text-foreground">Using your answers</p>
                  <p className="mt-2">Optional context helps the AI prioritize recommendations and tailor guidance for your audience.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {step === 'focus' && (
          <Card className="border-border/50 bg-card/90 backdrop-blur max-w-3xl mx-auto">
            <CardHeader>
              <CardTitle className="text-xl font-semibold">Step 3 · Analysis focus</CardTitle>
              <CardDescription>Choose whether you'd like the AI to focus more on content or design.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              <div className="space-y-6">
                <div className="rounded-lg border border-border bg-muted/30 p-8">
                  <div className="space-y-4">
                    <div className="text-center">
                      <h3 className="text-sm font-semibold text-foreground mb-2">Choose Analysis Type</h3>
                      <p className="text-xs text-muted-foreground">Select what aspects you want analyzed</p>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <button
                        type="button"
                        onClick={() => setAnalysisFocus('content')}
                        className={`p-4 rounded-lg border-2 transition-all ${analysisFocus === 'content'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-md'
                          : 'border-border bg-background/60 hover:border-blue-300 hover:bg-blue-50/50'
                          }`}
                      >
                        <div className="text-center space-y-2">
                          <div className={`text-2xl font-bold ${analysisFocus === 'content' ? 'text-blue-600' : 'text-foreground'}`}>
                            📝
                          </div>
                          <div className={`text-sm font-semibold ${analysisFocus === 'content' ? 'text-blue-700 dark:text-blue-400' : 'text-foreground'}`}>
                            Content Only
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Message & narrative
                          </div>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => setAnalysisFocus('design')}
                        className={`p-4 rounded-lg border-2 transition-all ${analysisFocus === 'design'
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 shadow-md'
                          : 'border-border bg-background/60 hover:border-purple-300 hover:bg-purple-50/50'
                          }`}
                      >
                        <div className="text-center space-y-2">
                          <div className={`text-2xl font-bold ${analysisFocus === 'design' ? 'text-purple-600' : 'text-foreground'}`}>
                            🎨
                          </div>
                          <div className={`text-sm font-semibold ${analysisFocus === 'design' ? 'text-purple-700 dark:text-purple-400' : 'text-foreground'}`}>
                            Design Only
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Visual & layout
                          </div>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => setAnalysisFocus('both')}
                        className={`p-4 rounded-lg border-2 transition-all ${analysisFocus === 'both'
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/20 shadow-md'
                          : 'border-border bg-background/60 hover:border-green-300 hover:bg-green-50/50'
                          }`}
                      >
                        <div className="text-center space-y-2">
                          <div className={`text-2xl font-bold ${analysisFocus === 'both' ? 'text-green-600' : 'text-foreground'}`}>
                            ⚡
                          </div>
                          <div className={`text-sm font-semibold ${analysisFocus === 'both' ? 'text-green-700 dark:text-green-400' : 'text-foreground'}`}>
                            Content + Design
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Complete analysis
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>

                  <div className="mt-6 rounded-lg bg-background/60 p-4">
                    <p className="text-sm text-muted-foreground">
                      {analysisFocus === 'content' && (
                        <>
                          <span className="font-semibold text-blue-600">Content Only</span>
                          <br />
                          The AI will analyze messaging clarity, narrative flow, text density, headline effectiveness, and storytelling structure. Design analysis will be skipped.
                        </>
                      )}
                      {analysisFocus === 'both' && (
                        <>
                          <span className="font-semibold text-green-600">Content + Design Analysis</span>
                          <br />
                          The AI will provide comprehensive analysis covering both content quality, clarity, narrative structure, and visual design, hierarchy, and aesthetic effectiveness.
                        </>
                      )}
                      {analysisFocus === 'design' && (
                        <>
                          <span className="font-semibold text-purple-600">Design Only</span>
                          <br />
                          The AI will analyze visual hierarchy, layout effectiveness, color palette, typography, white space, image quality, and design system coherence. Content analysis will be skipped.
                        </>
                      )}
                    </p>
                  </div>

                  {/* Video Content Checkbox */}
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={hasVideos}
                        onChange={(e) => setHasVideos(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-border text-accent focus:ring-accent focus:ring-offset-0"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-foreground">
                          This presentation contains videos
                        </span>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Video files can cause rendering issues. We'll use placeholder thumbnails instead.
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Fast Analysis Checkbox */}
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={fastAnalysis}
                        onChange={(e) => setFastAnalysis(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-border text-accent focus:ring-accent focus:ring-offset-0"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-foreground">
                          Faster Analysis (Recommended)
                        </span>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Generates thumbnails for the first 3 slides immediately. The rest will load on demand. Uncheck for full upfront generation (slower).
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Generate Edited Preview Checkbox */}
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <div className="space-y-3">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={generateEditedPreview}
                          onChange={(e) => setGenerateEditedPreview(e.target.checked)}
                          className="mt-0.5 h-4 w-4 rounded border-border text-accent focus:ring-accent focus:ring-offset-0"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-foreground">
                            Generate Edited Preview (Nano Banana Pro)
                          </span>
                          <p className="mt-1 text-xs text-muted-foreground">
                            Generate AI-enhanced slide previews using Google Gemini. Generates previews for first 5 slides.
                          </p>
                        </div>
                      </label>

                      {/* Model Selection - shown when checkbox is enabled */}
                      {generateEditedPreview && (
                        <div className="ml-7 space-y-2">
                          <label className="text-xs font-medium text-foreground">Model Selection</label>
                          <div className="space-y-2">
                            <label className="flex items-start gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="nanoBananaModel"
                                value="flash"
                                checked={nanoBananaModel === 'flash'}
                                onChange={(e) => setNanoBananaModel(e.target.value)}
                                className="mt-0.5 h-3 w-3 text-accent focus:ring-accent focus:ring-offset-0"
                              />
                              <div className="flex-1">
                                <span className="text-xs font-medium text-foreground">Gemini 2.5 Flash</span>
                                <p className="text-xs text-muted-foreground">Fast generation (~30s per slide), good quality</p>
                              </div>
                            </label>
                            <label className="flex items-start gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="nanoBananaModel"
                                value="pro"
                                checked={nanoBananaModel === 'pro'}
                                onChange={(e) => setNanoBananaModel(e.target.value)}
                                className="mt-0.5 h-3 w-3 text-accent focus:ring-accent focus:ring-offset-0"
                              />
                              <div className="flex-1">
                                <span className="text-xs font-medium text-foreground">Gemini 3 Pro Image ⭐</span>
                                <p className="text-xs text-muted-foreground">Premium quality with "thinking" process. May timeout during high demand.</p>
                              </div>
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Expertise Level Selection */}
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <div className="space-y-3">
                      <div>
                        <h3 className="text-sm font-semibold text-foreground mb-1">
                          🎯 How do you prefer your feedback?
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          Choose the style that works best for you
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setExpertiseLevel('beginner')}
                          className={`p-4 rounded-lg border-2 transition-all text-left ${expertiseLevel === 'beginner'
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-md'
                            : 'border-border bg-background/60 hover:border-blue-300 hover:bg-blue-50/50'
                            }`}
                        >
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xl">💬</span>
                              <span className={`text-sm font-semibold ${expertiseLevel === 'beginner' ? 'text-blue-700 dark:text-blue-400' : 'text-foreground'}`}>
                                Keep it simple
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              Plain language, easy to understand, no jargon
                            </p>
                          </div>
                        </button>

                        <button
                          type="button"
                          onClick={() => setExpertiseLevel('expert')}
                          className={`p-4 rounded-lg border-2 transition-all text-left ${expertiseLevel === 'expert'
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 shadow-md'
                            : 'border-border bg-background/60 hover:border-purple-300 hover:bg-purple-50/50'
                            }`}
                        >
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xl">🔬</span>
                              <span className={`text-sm font-semibold ${expertiseLevel === 'expert' ? 'text-purple-700 dark:text-purple-400' : 'text-foreground'}`}>
                                I speak design
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              Technical terms, design principles, detailed analysis
                            </p>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-border/40 pt-4">
                <Button variant="outline" onClick={() => setStep('questions')} className="border-border text-muted-foreground hover:text-foreground">
                  Back
                </Button>
                <Button onClick={handleCompleteWithQuestions} className="bg-gradient-to-r from-accent to-accent/90 text-accent-foreground hover:from-accent/90 hover:to-accent/80 shadow-lg">
                  Start analysis
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

      </div>
    </div>
  );
}

